import { __assign, __read, __rest } from "tslib";
import React, { useState, useEffect, useRef, useCallback } from "react";
import classNames from "classnames";
import moment from "moment";
import { useDefaultValue } from "../form/controlled";
import CalendarPart from "../calendar/CalendarPart";
import { CalendarTable, getTimeRange } from "../calendar/CalendarTable";
import { CalendarWrapper } from "./CalendarWrapper";
import { Input } from "../input/Input";
import { useTranslation } from "../i18n";
import { getYearMonthDate, DatePickerTrigger, isSame, isAfter, isBefore, isValidDate, isValidMonth, } from "./util";
import { genShowHourMinuteSecond, getHourMinuteSecond, getValidTimeValue, isValidTimeValue, } from "../timepicker/util";
import { DropdownBox } from "../dropdown";
import { useDefault } from "../_util/use-default";
import { Popover } from "../popover/Popover";
import { useConfig } from "../_util/config-context";
import { Icon } from "../icon";
import { noop } from "../_util/noop";
import { RangePickerContext } from "./RangePickerContext";
import { forwardRefWithStatics } from "../_util/forward-ref-with-statics";
import { mergeEventProps } from "../_util/merge-event-props";
import { KeyMap } from "../_util/key-map";
var getFormat = function (showTime, calendarType) {
    if (calendarType === "date") {
        return showTime ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD";
    }
    if (calendarType === "month") {
        return "YYYY-MM";
    }
    return "YYYY";
};
export function isValidRangeValue(value) {
    return (Array.isArray(value) &&
        moment.isMoment(value[0]) &&
        value[0].isValid() &&
        moment.isMoment(value[1]) &&
        value[1].isValid());
}
var defaultDateRender = function (_, text) { return text; };
export var RangePicker = forwardRefWithStatics(function RangePicker(props, ref) {
    var _a;
    var t = useTranslation(moment);
    var classPrefix = useConfig().classPrefix;
    var _b = useDefaultValue(props, [null, null]), header = _b.header, className = _b.className, style = _b.style, value = _b.value, onChange = _b.onChange, showTime = _b.showTime, disabled = _b.disabled, _c = _b.separator, separator = _c === void 0 ? "~" : _c, _format = _b.format, _d = _b.placeholder, placeholder = _d === void 0 ? showTime ? t.selectTime : t.selectDate : _d, _e = _b.defaultOpen, defaultOpen = _e === void 0 ? false : _e, open = _b.open, _f = _b.onOpenChange, onOpenChange = _f === void 0 ? noop : _f, _g = _b.onInputValueChange, onInputValueChange = _g === void 0 ? noop : _g, _h = _b.placement, placement = _h === void 0 ? "bottom-start" : _h, _j = _b.placementOffset, placementOffset = _j === void 0 ? 5 : _j, _k = _b.closeOnScroll, closeOnScroll = _k === void 0 ? true : _k, escapeWithReference = _b.escapeWithReference, popupContainer = _b.popupContainer, overlayClassName = _b.overlayClassName, overlayStyle = _b.overlayStyle, clearable = _b.clearable, _l = _b.calendarType, calendarType = _l === void 0 ? "date" : _l, _m = _b.dateRender, dateRender = _m === void 0 ? defaultDateRender : _m, restProps = __rest(_b, ["header", "className", "style", "value", "onChange", "showTime", "disabled", "separator", "format", "placeholder", "defaultOpen", "open", "onOpenChange", "onInputValueChange", "placement", "placementOffset", "closeOnScroll", "escapeWithReference", "popupContainer", "overlayClassName", "overlayStyle", "clearable", "calendarType", "dateRender"]);
    var parentUnit = ["month", "quarter"].includes(calendarType)
        ? "year"
        : "month";
    var _o = __read(useState(false), 2), hover = _o[0], setHover = _o[1];
    var format = _format || getFormat(showTime, calendarType);
    // 当前面板类型
    var _p = __read(useState([
        calendarType,
        calendarType,
    ]), 2), type = _p[0], setType = _p[1];
    // 当前左/右面板展示时间
    var _q = __read(useState(getDefaultViewMoment(0)), 2), curStartView = _q[0], setStartCurView = _q[1];
    var _r = __read(useState(getDefaultViewMoment(1)), 2), curEndView = _r[0], setEndCurView = _r[1];
    // 当前选中日期
    var _s = __read(useState(isValidRangeValue(value)
        ? [value[0].clone(), value[1].clone()]
        : [null, null]), 2), curValue = _s[0], setCurValue = _s[1];
    // 当前 hover 的日期
    var _t = __read(useState(null), 2), hovered = _t[0], setHovered = _t[1];
    // 上次选中日期
    var preValidValueRef = useRef(value || [null, null]);
    // 选择器是否展开
    var _u = __read(useDefault(open, defaultOpen, onOpenChange), 2), active = _u[0], setActive = _u[1];
    /** 是否显示毫秒 */
    var showMilliseconds = genShowHourMinuteSecond(format).showMilliseconds;
    // 输入框显示值
    var startInputRef = useRef(null);
    var endInputRef = useRef(null);
    // 2.7.4 后改为两个输入框显示
    var getInputValue = useCallback(function (value) {
        var _a = __read(value || [null, null], 2), start = _a[0], end = _a[1];
        if (moment.isMoment(start) && moment.isMoment(end)) {
            var startMoment = start.locale(t.locale);
            var endMoment = end.locale(t.locale);
            if (calendarType === "quarter") {
                return {
                    inputValueStart: startMoment.format("YYYY") + "-Q" + startMoment.quarter(),
                    separator: separator,
                    inputValueEnd: endMoment.format("YYYY") + "-Q" + endMoment.quarter(),
                };
            }
            return {
                inputValueStart: startMoment.format(format),
                separator: separator,
                inputValueEnd: endMoment.format(format),
            };
        }
        return "";
    }, [calendarType, format, separator, t.locale]);
    var _v = __read(useState(getInputValue(curValue)), 2), inputValue = _v[0], setInputValue = _v[1];
    useEffect(function () {
        setCurValue(isValidRangeValue(value)
            ? [value[0].clone(), value[1].clone()]
            : [null, null]);
        // [TODO] moment 更改后直接获取值（format）可能拿到是之前值？
        var timer = setTimeout(function () { return setInputValue(getInputValue(value)); }, 0);
        return function () {
            clearTimeout(timer);
        };
    }, [format, separator, value, getInputValue, active]);
    useEffect(function () {
        preValidValueRef.current = value || [null, null];
    }, [value]);
    var timerRef = useRef(null);
    useEffect(function () { return function () {
        clearTimeout(timerRef.current);
    }; }, []);
    useEffect(function () {
        var _a, _b;
        // 同一个月不跳转面板 | 同一个年不跳转面板
        var limit = calendarType === "date" ? "M" : "y";
        if (!((_a = curValue[0]) === null || _a === void 0 ? void 0 : _a.isSame(curValue[1], limit)) && curValue[1]) {
            setStartCurView(curValue[0]);
        }
        if (!((_b = curValue[1]) === null || _b === void 0 ? void 0 : _b.isSame(curValue[0], limit)) && curValue[1]) {
            setEndCurView(curValue[1]);
        }
    }, [calendarType, curValue]);
    function handleChange(value, context) {
        // 同步日期/时间
        if (showTime && isValidRangeValue(value)) {
            value = syncDate(value, context.type); // eslint-disable-line no-param-reassign
        }
        setCurValue(value);
        // [TODO] moment 更改后直接获取值（format）可能拿到是之前值？
        timerRef.current = setTimeout(function () { return setInputValue(getInputValue(value)); }, 0);
        // 没有时间选择时没有二次确认选中
        if (!showTime && isValidRangeValue(value)) {
            onChange(value, context);
            handleClose();
        }
    }
    function handleOk(event) {
        var value = curValue;
        if (isValidRangeValue(curValue) && isAfter(curValue[0], curValue[1])) {
            value = [curValue[1], curValue[0]];
            setCurValue(value);
        }
        onChange(value, { event: event });
        handleClose();
    }
    function handleOpen() {
        if (disabled) {
            return;
        }
        setActive(true);
        setType([calendarType, calendarType]);
    }
    function handleClose() {
        setActive(false);
    }
    /**
     * 同步日期/时间
     * 如果当前修改为日期，则同步上次时间并修正；
     * 如果当前修改为时间，则同步上次日期；
     */
    function syncDate(value, type) {
        var preValidValue = preValidValueRef.current;
        var range = props.range, _a = props.disabledTime, disabledTime = _a === void 0 ? function () { return ({}); } : _a;
        // 如果包含上次选择，则以上次选择为基准同步
        if (isValidRangeValue(preValidValue)) {
            if (type === "date") {
                var timeStart = getValidTimeValue(preValidValue[0], __assign(__assign({ range: getTimeRange(value[0], range) }, disabledTime(value, "start")), { hourStep: (showTime === null || showTime === void 0 ? void 0 : showTime.hourStep) || 1, minuteStep: (showTime === null || showTime === void 0 ? void 0 : showTime.minuteStep) || 1, secondStep: (showTime === null || showTime === void 0 ? void 0 : showTime.secondStep) || 1 }));
                var timeEnd = getValidTimeValue(preValidValue[1], __assign(__assign({ range: getTimeRange(value[1], range) }, disabledTime(value, "end")), { hourStep: (showTime === null || showTime === void 0 ? void 0 : showTime.hourStep) || 1, minuteStep: (showTime === null || showTime === void 0 ? void 0 : showTime.minuteStep) || 1, secondStep: (showTime === null || showTime === void 0 ? void 0 : showTime.secondStep) || 1 }));
                value[0].set(getHourMinuteSecond(timeStart));
                value[1].set(getHourMinuteSecond(timeEnd));
            }
            if (type === "time") {
                value[0].set(getYearMonthDate(preValidValue[0]));
                value[1].set(getYearMonthDate(preValidValue[1]));
            }
            // 如果是首次选择，则将当前时间进行修正
        }
        else if (type === "date") {
            // 合并 showTime.defaultValue
            if (isValidRangeValue(showTime === null || showTime === void 0 ? void 0 : showTime.defaultValue)) {
                var _b = __read(showTime.defaultValue, 2), start = _b[0], end = _b[1];
                value[0].set(getHourMinuteSecond(start));
                value[1].set(getHourMinuteSecond(end));
            }
            var timeStart = getValidTimeValue(value[0], __assign(__assign({ range: getTimeRange(value[0], range) }, disabledTime(value, "start")), { hourStep: (showTime === null || showTime === void 0 ? void 0 : showTime.hourStep) || 1, minuteStep: (showTime === null || showTime === void 0 ? void 0 : showTime.minuteStep) || 1, secondStep: (showTime === null || showTime === void 0 ? void 0 : showTime.secondStep) || 1 }));
            var timeEnd = getValidTimeValue(value[1], __assign(__assign({ range: getTimeRange(value[1], range) }, disabledTime(value, "end")), { hourStep: (showTime === null || showTime === void 0 ? void 0 : showTime.hourStep) || 1, minuteStep: (showTime === null || showTime === void 0 ? void 0 : showTime.minuteStep) || 1, secondStep: (showTime === null || showTime === void 0 ? void 0 : showTime.secondStep) || 1 }));
            value[0].set(getHourMinuteSecond(timeStart));
            value[1].set(getHourMinuteSecond(timeEnd));
        }
        preValidValueRef.current = value;
        return value;
    }
    /**
     * 获取 ShowTime 参数
     * @param index 左/右面板 - 0/1
     */
    function getShowTime(index) {
        if (typeof showTime === "object") {
            if (Array.isArray(showTime.defaultValue)) {
                return __assign(__assign({}, showTime), { defaultValue: showTime.defaultValue[index] });
            }
            return showTime;
        }
        return !!showTime;
    }
    /**
     * 获取左右面板 range
     */
    function getRange(range) {
        var _a = __read(range || [null, null], 2), rangeMin = _a[0], rangeMax = _a[1];
        var endPreEnd = moment(curEndView)
            .subtract(1, parentUnit)
            .endOf(parentUnit);
        var startNxtStart = moment(curStartView)
            .add(1, parentUnit)
            .startOf(parentUnit);
        return [
            [rangeMin, isAfter(endPreEnd, rangeMax) ? rangeMax : endPreEnd],
            [
                isBefore(startNxtStart, rangeMin) ? rangeMin : startNxtStart,
                rangeMax,
            ],
        ];
    }
    /**
     * 获取面板默认展示时间
     * @param index 左/右面板 - 0/1
     */
    function getDefaultViewMoment(index) {
        var range = props.range;
        var showTime = getShowTime(index);
        var time = showTime && typeof showTime === "object"
            ? showTime.defaultValue
            : undefined;
        // 没有初始值
        if (!isValidRangeValue(value)) {
            var m = void 0;
            if (!time) {
                m = moment();
            }
            else {
                m = moment(getHourMinuteSecond(time));
            }
            // range 判断
            if (Array.isArray(range)) {
                var _a = __read(range, 2), start = _a[0], end = _a[1];
                if (!moment.isMoment(start)) {
                    start = moment(0);
                }
                if (!moment.isMoment(end)) {
                    end = moment(Math.pow(2, 52));
                }
                if (isBefore(end, m, parentUnit)) {
                    return index === 0 ? moment(end).subtract(1, parentUnit) : end;
                }
                if (isAfter(start, m, parentUnit)) {
                    return index === 0 ? start : moment(start).add(1, parentUnit);
                }
                if (moment.isMoment(start) && isSame(start, m, parentUnit)) {
                    return index === 0 ? m : moment(m).add(1, parentUnit);
                }
            }
            return index === 0 ? moment(m).subtract(1, parentUnit) : m;
        }
        // 初始值在同个月/年
        if (isSame(value[0], value[1], parentUnit)) {
            // 根据 range 判断
            if (Array.isArray(range)) {
                // 当前月前一月不可选时，向后显示一月
                if (moment.isMoment(range[0]) &&
                    isSame(range[0], value[1], parentUnit)) {
                    return index === 0 ? value[1] : moment(value[1]).add(1, parentUnit);
                }
            }
            return index === 0
                ? moment(value[1]).subtract(1, parentUnit)
                : value[1];
        }
        return value[index];
    }
    /**
     * 是否改变当前视图时间
     * 连续两月情况下，点击中间交错日期不改变当前视图时间
     * @param from 当前视图变更事件来源
     * @param nextDate 要变更到的时间
     * @param type 当前视图类型
     */
    function shouldCurViewChange(from, nextDate, type) {
        if (type === void 0) { type = "start"; }
        if (from !== "outside-date") {
            return true;
        }
        var diffMonths = function (start, end) {
            var years = end.year() - start.year();
            return end.month() - start.month() + years * 12;
        };
        if (diffMonths(curStartView, curEndView) === 1) {
            if (type === "start") {
                return diffMonths(curStartView, nextDate) !== 1;
            }
            return diffMonths(nextDate, curEndView) !== 1;
        }
        return true;
    }
    function handleStartInputChange(content) {
        var _a;
        setInputValue(__assign(__assign({}, inputValue), { inputValueStart: content }));
        var startValue = moment(content, format, true);
        var endValue = moment(inputValue.inputValueEnd, format, true);
        var temp;
        if (!startValue.isValid()) {
            onInputValueChange(content, { valid: false, partial: "start" });
            return;
        }
        var isValid = calendarType === "month" ? isValidMonth : isValidDate;
        var valid = isValid(startValue, props, endValue);
        var validTime = showTime
            ? isValidTimeValue(startValue, __assign(__assign(__assign({}, showTime), props), (_a = props.disabledTime) === null || _a === void 0 ? void 0 : _a.call(props, [startValue, endValue], "start")))
            : true;
        if (valid && validTime) {
            // 输入的开始日期大于结束日期互换
            if (startValue.isAfter(endValue, "d")) {
                temp = endValue;
                endValue = startValue;
                startValue = temp;
            }
            setCurValue([startValue, endValue]);
        }
        onInputValueChange(content, {
            valid: true,
            partial: "start",
            value: valid && validTime ? [startValue, endValue] : undefined,
        });
    }
    function handleEndInputChange(content) {
        var _a;
        setInputValue(__assign(__assign({}, inputValue), { inputValueEnd: content }));
        var startValue = moment(inputValue.inputValueStart, format, true);
        var endValue = moment(content, format, true);
        var temp;
        if (!endValue.isValid()) {
            onInputValueChange(content, { valid: false, partial: "end" });
            return;
        }
        var isValid = calendarType === "month" ? isValidMonth : isValidDate;
        var valid = isValid(endValue, props, startValue);
        var validTime = showTime
            ? isValidTimeValue(endValue, __assign(__assign(__assign({}, showTime), props), (_a = props.disabledTime) === null || _a === void 0 ? void 0 : _a.call(props, [startValue, endValue], "end")))
            : true;
        if (valid && validTime) {
            // 输入的结束日期小于开始日期互换
            if (endValue.isBefore(startValue, "d")) {
                temp = endValue;
                endValue = startValue;
                startValue = temp;
            }
            setCurValue([startValue, endValue]);
        }
        onInputValueChange(content, {
            valid: true,
            partial: "end",
            value: valid && validTime ? [startValue, endValue] : undefined,
        });
    }
    function handleKeyDown(event) {
        if (!active) {
            handleOpen();
            return;
        }
        switch (event.key) {
            case KeyMap.Enter:
                handleOk(event);
                break;
            case KeyMap.Esc:
                handleClose();
                break;
        }
    }
    return (React.createElement(Popover, { trigger: [
            DatePickerTrigger,
            { onOpen: handleOpen, onClose: handleClose },
        ], visible: active, onVisibleChange: setActive, placement: placement, placementOffset: placementOffset, closeOnScroll: closeOnScroll, escapeWithReference: escapeWithReference, popupContainer: popupContainer, overlayClassName: overlayClassName, overlayStyle: overlayStyle, overlay: React.createElement(RangePickerContext.Provider, { value: { range: restProps.range } },
            React.createElement(DropdownBox, { preventMouseDown: true, adaptable: true },
                React.createElement(CalendarPart.Panel, { rangeMode: true, timeMode: type[0] === "time" },
                    !!header && (React.createElement(CalendarPart.Header, null, header)),
                    React.createElement(CalendarWrapper, __assign({}, restProps, { showTime: showTime, value: curValue, onChange: handleChange, onOk: handleOk, type: type, onTypeChange: function (types) {
                            return setType(types);
                        } }), function (_a) {
                        var type = _a.type, onTypeChange = _a.onTypeChange, range = _a.range, props = __rest(_a, ["type", "onTypeChange", "range"]);
                        var _b = __read(getRange(range), 2), startRange = _b[0], endRange = _b[1];
                        return (React.createElement(React.Fragment, null,
                            React.createElement(CalendarTable, __assign({}, props, { monthOnly: calendarType === "month", rangeType: "start", range: startRange, type: type[0], onTypeChange: function (t) { return onTypeChange([t, type[1]]); }, showTime: getShowTime(0), curViewMoment: curStartView, onCurViewMomentChange: function (date, _a) {
                                    var from = (_a === void 0 ? {} : _a).from;
                                    if (!shouldCurViewChange(from, date)) {
                                        return;
                                    }
                                    if (!isAfter(curEndView, date, parentUnit)) {
                                        setEndCurView(moment(date).add(1, parentUnit));
                                    }
                                    setStartCurView(date);
                                }, dateRangeInRangePicker: range || [null, null], hovered: hovered, onHoveredChange: setHovered, dateRender: function (date, text) {
                                    return dateRender(date, text, { partial: "start" });
                                } })),
                            React.createElement(CalendarTable, __assign({}, props, { monthOnly: calendarType === "month", rangeType: "end", range: endRange, type: type[1], onTypeChange: function (t) { return onTypeChange([type[0], t]); }, showTime: getShowTime(1), curViewMoment: curEndView, onCurViewMomentChange: function (date, _a) {
                                    var from = (_a === void 0 ? {} : _a).from;
                                    if (!shouldCurViewChange(from, date, "end")) {
                                        return;
                                    }
                                    if (!isBefore(curStartView, date, parentUnit)) {
                                        setStartCurView(moment(date).subtract(1, parentUnit));
                                    }
                                    setEndCurView(date);
                                }, dateRangeInRangePicker: range || [null, null], hovered: hovered, onHoveredChange: setHovered, dateRender: function (date, text) {
                                    return dateRender(date, text, { partial: "end" });
                                } }))));
                    })))) },
        React.createElement("div", __assign({ ref: ref, className: classNames(classPrefix + "-date" + (showTime ? "time" : "") + "picker", className, {
                "is-disabled": disabled,
            }), style: style }, mergeEventProps(props)),
            React.createElement("div", { className: classNames(classPrefix + "-date" + (showTime ? "time" : "") + "picker__input", classPrefix + "-datepicker__input--range", (_a = {
                        "is-hover": hover && clearable && isValidRangeValue(value)
                    },
                    _a[classPrefix + "-datetimepicker__input--millisecond"] = showTime && showMilliseconds,
                    _a)), onMouseEnter: function () { return setHover(true); }, onMouseLeave: function () { return setHover(false); } },
                React.createElement(Input, { ref: startInputRef, disabled: disabled, placeholder: Array.isArray(placeholder) ? placeholder[0] : placeholder, value: typeof inputValue === "string"
                        ? inputValue
                        : inputValue.inputValueStart, onChange: handleStartInputChange, onKeyDown: handleKeyDown, onFocus: function () {
                        return calendarType === "quarter" && startInputRef.current.blur();
                    }, onClick: function (event) {
                        if (active) {
                            event.stopPropagation();
                        }
                    } }),
                typeof inputValue !== "string" && (React.createElement("span", null, inputValue.separator)),
                React.createElement(Input, { ref: endInputRef, disabled: disabled, placeholder: Array.isArray(placeholder) ? placeholder[1] : placeholder, value: typeof inputValue === "string"
                        ? inputValue
                        : inputValue.inputValueEnd, onChange: handleEndInputChange, onKeyDown: handleKeyDown, onFocus: function () {
                        return calendarType === "quarter" && endInputRef.current.blur();
                    }, onClick: function (event) {
                        if (active) {
                            event.stopPropagation();
                        }
                    } }),
                clearable && !disabled && (React.createElement(Icon, { type: "dismiss", onClick: function (event) {
                        event.stopPropagation();
                        onChange([null, null], { event: event });
                    } }))))));
}, { defaultLabelAlign: "middle" });
RangePicker.displayName = "RangePicker";
//# sourceMappingURL=RangePicker.js.map